import { useSuspenseQuery } from '@tanstack/react-query'
import { Link, createFileRoute } from '@tanstack/react-router'
import { ChevronLeft } from 'lucide-react'
import { TemplateEditor } from '@/components/templates/TemplateEditor'
import { accountQuery, reportTemplateQuery } from '@/lib/queries'

/**
 * The template editor page (CLI-643). A full-page surface rather than a section of the account
 * page — the `_` in the filename un-nests it from the account detail route — because the editor
 * is a two-pane workspace (block stack + live preview) that needs the width. Designed to slot
 * into the report-configuration flow when reports land here; until then the account page's
 * templates section is the way in.
 */
export const Route = createFileRoute('/app/accounts/$accountId_/templates/$templateId')({
  loader: async ({ context, params }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(accountQuery(params.accountId)),
      context.queryClient.ensureQueryData(reportTemplateQuery(params.accountId, params.templateId)),
    ])
  },
  component: TemplateEditorPage,
})

function TemplateEditorPage() {
  const { accountId, templateId } = Route.useParams()
  const { data: account } = useSuspenseQuery(accountQuery(accountId))
  const { data: template } = useSuspenseQuery(reportTemplateQuery(accountId, templateId))

  return (
    <div className="flex flex-col gap-4">
      <Link
        to="/app/accounts/$accountId"
        params={{ accountId }}
        className="flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="size-3.5" />
        {account.name}
      </Link>
      {/* Keyed by id so navigating between templates never carries one draft into another. */}
      <TemplateEditor
        key={template.id}
        scope={{
          kind: 'account',
          accountId,
          accountName: account.name,
          brand: account.brand,
        }}
        template={template}
      />
    </div>
  )
}
